import { parse } from 'mf-parser';
import { hasSideChainSite, isFragment } from "./getFragment.js";
/**
 * Split a `chemical-groups` notation such as `HAlaGlyOH` or `HODampDcmpH` into
 * the groups it is made of, expanding parentheses and multipliers.
 * @param ocl - openchemlib.
 * @param notation - the group notation.
 * @returns one token per group, in order.
 */
export function parseGroupNotation(ocl, notation) {
    const trimmed = notation.trim();
    if (trimmed === '')
        return [];
    return expandNotation(ocl, parse(trimmed)).map(({ symbol, sideChain }) => {
        if (!isFragment(ocl, symbol)) {
            throw new Error(`unknown group in sequence: ${symbol}`);
        }
        if (sideChain === undefined)
            return { symbol };
        if (!isFragment(ocl, sideChain)) {
            throw new Error(`unknown group in sequence: ${sideChain}`);
        }
        return { symbol, sideChain };
    });
}
/**
 * Repeat the block a multiplier applies to, so `H(Ala)3OH` becomes three `Ala`,
 * and hand a parenthesis that carries no multiplier to the group it follows, so
 * the `Ph` of `Cysp(Ph)` becomes that residue's side chain.
 * @param ocl - openchemlib.
 * @param tokens - the tokens `mf-parser` produced.
 * @returns the groups of the notation, in order.
 */
function expandNotation(ocl, tokens) {
    const stack = [{ symbols: [], last: [] }];
    for (const [index, token] of tokens.entries()) {
        const frame = stack.at(-1);
        if (frame === undefined)
            throw new Error('unbalanced parenthesis');
        switch (token.kind) {
            case 'atom': {
                const group = { symbol: String(token.value) };
                frame.last = [group];
                frame.symbols.push(group);
                break;
            }
            case 'openingParenthesis':
                stack.push({ symbols: [], last: [] });
                break;
            case 'closingParenthesis': {
                const closed = stack.pop();
                const parent = stack.at(-1);
                if (closed === undefined || parent === undefined) {
                    throw new Error('unbalanced parenthesis');
                }
                closeParenthesis(ocl, closed.symbols, parent, tokens[index + 1]);
                break;
            }
            case 'multiplier': {
                const count = Number(token.value);
                for (let repeat = 1; repeat < count; repeat++) {
                    // Cloned, so a side chain written after the repeat lands on the last
                    // copy alone.
                    frame.symbols.push(...frame.last.map((group) => ({ ...group })));
                }
                break;
            }
            default:
                break;
        }
    }
    if (stack.length !== 1)
        throw new Error('unbalanced parenthesis');
    const root = stack[0];
    if (root === undefined)
        throw new Error('unbalanced parenthesis');
    return root.symbols;
}
/**
 * Fold a parenthesis that just closed into the frame around it.
 *
 * A parenthesis a multiplier follows is a block to repeat, and so is one that
 * follows a symbol with no side chain site — `H(Ala)3OH` and `H(Ala)OH` both
 * write the chain itself. One that follows a triradical residue instead names
 * what hangs off it.
 * @param ocl - openchemlib.
 * @param closed - the groups the parenthesis collected.
 * @param parent - the frame the parenthesis sits in, updated in place.
 * @param parent.symbols - everything that frame holds.
 * @param parent.last - the block a multiplier there would repeat.
 * @param next - the token after the parenthesis, `undefined` at the end.
 */
function closeParenthesis(ocl, closed, parent, next) {
    const host = parent.symbols.at(-1);
    if (next?.kind === 'multiplier' ||
        host === undefined ||
        !hasSideChainSite(ocl, host.symbol)) {
        parent.last = closed;
        parent.symbols.push(...closed);
        return;
    }
    const side = closed[0];
    if (closed.length !== 1 || side === undefined) {
        const written = closed.map((group) => group.symbol).join('');
        throw new Error(`the side chain (${written}) is not a single group`);
    }
    host.sideChain = side.symbol;
    parent.last = [host];
}
//# sourceMappingURL=parseGroupNotation.js.map